import type { CumulativeGrowthPointView } from '@/types/coaching'

// Figma 16:2890 chart-area, drawn 1:1: 658×120, dashed rules at 12/55 (x 16–642), axis at 98 (x 15–648),
// first and last round labels centred on x 15.5 and 646.5.
const WIDTH = 658
const HEIGHT = 120
const GRID_LEFT = 16
const GRID_RIGHT = 642
const AXIS_LEFT = 15
const AXIS_RIGHT = 648
const FIRST_SLOT_X = 15.5
const LAST_SLOT_X = 646.5
const PLOT_TOP = 12
const PLOT_BOTTOM = 98
const LABEL_BASELINE = 118
const SLOTS = 10

interface Props {
  points: CumulativeGrowthPointView[]
  value: (point: CumulativeGrowthPointView) => number | null | undefined
  domain: [number, number]
  color: string
  roundLabel: (n: number) => string
  testId: string
}

function slotX(slot: number): number {
  return FIRST_SLOT_X + (slot * (LAST_SLOT_X - FIRST_SLOT_X)) / (SLOTS - 1)
}

/**
 * One growth line over the latest ten Lifetime Assessment Sequence slots (D11). Each point is that report's own
 * frozen figure, a missing value breaks the line instead of bridging it, and a dashed rule marks where the
 * course changes between consecutive points.
 */
export function CumulativeGrowthChart({ points, value, domain, color, roundLabel, testId }: Props) {
  const recent = points.slice(-SLOTS)
  const firstSequence = recent[0]?.sequence ?? 1
  const [min, max] = domain
  const y = (v: number) => {
    const clamped = Math.min(Math.max(v, min), max)
    return PLOT_BOTTOM - ((clamped - min) / (max - min)) * (PLOT_BOTTOM - PLOT_TOP)
  }
  const plotted = recent.map((point, slot) => {
    const v = value(point)
    return v == null ? null : { slot, v, x: slotX(slot), y: y(v) }
  })
  const segments: { x: number; y: number }[][] = []
  plotted.forEach((entry) => {
    if (!entry) {
      segments.push([])
      return
    }
    if (segments.length === 0) segments.push([])
    segments[segments.length - 1].push(entry)
  })
  const boundaries = recent.flatMap((point, slot) =>
    slot > 0 && point.courseTitle !== recent[slot - 1].courseTitle
      ? [{ x: (slotX(slot - 1) + slotX(slot)) / 2, title: point.courseTitle ?? '' }]
      : [],
  )

  return (
    <svg
      width={WIDTH}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="block overflow-visible"
      data-testid={testId}
      role="img"
    >
      {[PLOT_TOP, 55].map((gridY) => (
        <line key={gridY} x1={GRID_LEFT} x2={GRID_RIGHT} y1={gridY + 0.5} y2={gridY + 0.5} stroke="#d0d0d0" strokeDasharray="2 2" />
      ))}
      <line x1={AXIS_LEFT} x2={AXIS_RIGHT} y1={PLOT_BOTTOM + 0.5} y2={PLOT_BOTTOM + 0.5} stroke="#d0d0d0" />
      {boundaries.map((boundary) => (
        <g key={boundary.x} data-testid={`${testId}-course-boundary`}>
          <line x1={boundary.x} x2={boundary.x} y1={0} y2={PLOT_BOTTOM} stroke="#888" strokeDasharray="3 3" />
          <text x={boundary.x + 4} y={8} fontSize="9" fill="#888">{boundary.title}</text>
        </g>
      ))}
      {segments.filter((segment) => segment.length > 1).map((segment) => (
        <polyline
          key={`${segment[0].x}-${segment.length}`}
          points={segment.map((p) => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke={color}
          strokeWidth={2}
        />
      ))}
      {plotted.map((entry) =>
        entry ? (
          <g key={entry.slot}>
            <circle cx={entry.x} cy={entry.y} r={3} fill={color} stroke="#fff" strokeWidth={2} />
            <text x={entry.x} y={entry.y - 5.5} fontSize="13" fontWeight={700} fill={color} textAnchor="middle">
              {entry.v}
            </text>
          </g>
        ) : null,
      )}
      {[0, 4, 9].map((slot) => (
        <text
          key={slot}
          x={slotX(slot)}
          y={LABEL_BASELINE}
          fontSize={slot === 9 ? 12 : 13}
          fill={slot === 0 ? '#333' : '#b3b3b3'}
          textAnchor="middle"
        >
          {roundLabel(firstSequence + slot)}
        </text>
      ))}
    </svg>
  )
}
